/**
 * Helper: Redirects
 * ------------------------------------------------------------------------------
 * An event based redirect module that handles all global redirect requests.
 *
 * @namespace redirects
 */
import Cookies from 'js-cookie';

import {getQueryParamsObject} from '~/helpers/utils';

/**
 * Instance cookies.
 */
const cookieSelector = {
  geolocation: 'redirect_geolocation_v3',
  settings: 'redirect_store_settings_v3',
};

/**
 * Instance events.
 */
const eventSelector = {
  geoIPErrorHandler: 'Redirect:geoIPErrorHandler',
  locationDetected: 'Redirect:locationDetected',
  matched: 'Redirect:isMatchedLocation',
  unmatched: 'Redirect:isUnmatchedLocation',
};

/**
 * Export new redirect helper function.
 */
export default () => {

  /**
   * Initialise redirect scripts.
   * - If user's geolocation matches current store (Matched).
   * - If user doesn't match store (Unmatched).
   */
  function init() {
    if (typeof getSettingsCookie() !== 'undefined') {
      const settingsCookie = getSettingsCookie();
      const matchingStore = getMatchingStore(settingsCookie.loc.toUpperCase());

      if (
        matchingStore.storeCode !== window.theme.store.storeCode ||
        (matchingStore.storeCode === window.theme.store.storeCode &&
          window.theme.currentLanguage.toUpperCase() !== settingsCookie.lang.toUpperCase()
        )
      ) {
        const matchingStoreUrl = `${matchingStore.storeUrl}${isNewLanguage(matchingStore, settingsCookie)}${getNewPathName()}`;

        window.location = matchingStoreUrl;
        return;
      }
    }

    fetchUserGeolocation()
      .then((response) => {
        Frame.EventBus.emit(eventSelector.locationDetected, {
          location: response,
          matchingStore: getMatchingStore(response.countryCode, response.continentCode),
        });

        if (isMatchedLocation(response)) {
          emitMatchedLocationEvents(response);
          return response;
        }

        emitUnmatchedLocationEvents(response);
        return response;
      })
      .then((response) => {
        setGeolocationCookie(response);
        return response;
      })
      .catch((error) => {
        emitGeoIPErrorHandler();
        throw new Error('Could not fetch geolocation data from selected service.', error);
      });
  }

  /**
   * Fire events if user matches the store's supported locations.
   * - Set cookies if query parameters exist.
   * @param {Object} response - GeoIP response from ipstack.
   */
  function emitMatchedLocationEvents(response) {
    Frame.EventBus.emit(eventSelector.matched, response);
    const queryParameters = getQueryParamsObject();

    if (queryParameters.loc && queryParameters.lang) {
      Cookies.set(cookieSelector.settings, queryParameters, {domain: '.alessi.com'});
    }
  }

  /**
   * Emit events if user doesn't match the store's supported locations.
   * @param {Object} response - GeoIP response from ipstack.
   */
  function emitUnmatchedLocationEvents(response) {
    Frame.EventBus.emit(eventSelector.unmatched, response);
    Frame.EventBus.emit(eventSelector.locationDetected, {
      location: response,
      matchingStore: getMatchingStore(response.countryCode, response.continentCode),
    });
  }

  /**
   * Emit events if geolocation service does not return a response.
   */
  function emitGeoIPErrorHandler() {
    Frame.EventBus.emit(eventSelector.geoIPErrorHandler);
  }

  /**
   * Format user geolocation information from response.
   * @param {Object} response - ipstack.io successful response.
   * @returns {Object}
   */
  function getFormattedGeolocationData(response) {
    return {
      continentCode: response.continent.code,
      continentName: response.continent.names.en,
      countryCode: response.country.iso_code,
      countryName: response.country.names.en,
    };
  }

  /**
   * Set cookie events based on request.
   * @param {Object} response - ipstack.io successful response.
   */
  function setGeolocationCookie(response) {
    Cookies.set(cookieSelector.geolocation, response, {expires: 7});
  }

  /**
   * Get geolocation cookies.
   * @returns {Object}
   */
  function getGeolocationCookie() {
    return Cookies.getJSON(cookieSelector.geolocation);
  }

  /**
   * Get settings cookies.
   * @returns {Object}
   */
  function getSettingsCookie() {
    return Cookies.getJSON(cookieSelector.settings);
  }

  /**
   * Return Geolocation data of current user.
   * - Check theme settings of geolocation service.
   * - Make request to either MaxMind or ipstack and resolve formatted data.
   */
  function fetchUserGeolocation() {
    return new Promise((resolve, reject) => {

      /**
       * Use cached geolocation data if it has already been requested.
       */
      if (typeof getGeolocationCookie() !== 'undefined') {
        resolve(getGeolocationCookie());
        return;
      }

      /**
       * Make a request to MaxMind and cache in a cookie.
       */
      const onSuccess = (response) => {
        const formattedData = getFormattedGeolocationData(response);

        resolve(formattedData);
        return formattedData;
      };

      const onError = (error) => {
        reject(error);
      };

      geoip2.country(onSuccess, onError);
    });
  }

  /**
   * Get the matching store based on the user's location.
   * - Will check countries then supported stores, then defaulting to continent.
   * @param {Object} response - Formatted store and redirect country codes from settings.
   * @returns {Object}
   */
  function getMatchingStore(countryCode, continentCode) {
    const matchingCountries = Frame.MultiStore.filter((element) => {
      return (element.storeCode === countryCode);
    });

    const matchingSupportedStores = Frame.MultiStore.filter((element) => {
      return (element.supportedStores.includes(countryCode));
    });

    const matchingContinents = Frame.MultiStore.filter((element) => {
      return (element.storeCode === continentCode);
    });

    /**
     * If there is no matching store for the country then fallback
     * to a supported store.
     */
    if (matchingSupportedStores.length > 0) {
      return matchingSupportedStores[0];
    }

    if (matchingCountries.length <= 0) {
      const foundContinents = matchingContinents.filter((element) => {
        return (!element.storeCode);
      });

      if (foundContinents.length <= 0) {
        return matchingContinents[0];
      }
      return foundContinents[0];
    }

    return matchingCountries[0];
  }

  /**
   * Check if the users geolocation matches the store's location or supported locations.
   * @param {Object} response - GeoIP response from ipstack.
   * @returns {Boolean}
   */
  function isMatchedLocation(response) {
    return (getMatchingStore(response.countryCode).storeCode === theme.store.storeCode);
  }

  /**
   * Generate the new pathname removing the language part if in langify.
   */
  function getNewPathName() {
    const pathnameArray = window.location.pathname.split('/');
    return (window.theme.currentLanguage === pathnameArray[1])
      ? window.location.pathname.substring(3)
      : window.location.pathname;
  }

  /**
   * Generate the language url params string.
   * @param {Object} matchingStore - Matching website.
   * @param {Object} settingsCookie - Settings cookie.
   */
  function isNewLanguage(matchingStore, settingsCookie) {
    return (multipleOptions(matchingStore.supportedLanguages) && !isDefaultLanguage(settingsCookie.lang, matchingStore.defaultLanguage))
      ? `/${settingsCookie.lang.toLowerCase()}`
      : '';
  }

  /**
   * Check if the selected website has more than one language.
   * @param {Object} languages - languages
   */
  function multipleOptions(languages) {
    return languages.length > 1;
  }

  /**
   * @param {*} settingsCookieLang
   * @param {*} matchingStoreLang
   */
  function isDefaultLanguage(settingsCookieLang, matchingStoreLang) {
    return settingsCookieLang === matchingStoreLang;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    getMatchingStore,
    init,
  });
};
